import { api } from "../api";
import { profilePushRow } from "../push";
import { S, on, render } from "../state";
import { $, avatar, badge, closeSheet, edHead, esc, openSheet, toast, wirePhotos } from "../ui";
import type { MeResponse } from "../../shared/types";
import { birthdayField, classOfField, collectProfile, interestsField, nameFields, phoneField } from "./fields";

/** Photo picker on Finish Your Profile and the Profile page. Only the person can upload their own photo. */
export function photoBlock(optional = false) {
  const u = S.me!.user;
  return `<div class="photoedit" id="photobox">${avatar(u.id, "big", S.photoBust)}<div class="stack" style="gap:8px">
    <b>Your Photo${optional ? ' <span class="muted" style="font-weight:400">(Optional)</span>' : ""}</b><div class="muted small">Shown next to your name on events and lists.</div>
    <div class="row wrap" style="gap:8px"><label class="btn sm" style="cursor:pointer">${u.photo ? "Change Photo" : "Upload Photo"}<input type="file" accept="image/*" data-change="pick-photo" style="display:none"></label>
    ${u.photo ? '<button class="btn ghost sm" type="button" data-act="remove-photo">Remove Photo</button>' : ""}</div></div></div>`;
}

/** On Finish Your Profile, swap just the photo box so typed answers aren't lost. */
function refreshPhoto() {
  const box = $("#photobox");
  if (box && !S.me!.user.profileComplete) {
    box.outerHTML = photoBlock(true);
    wirePhotos($("#photobox")!);
  } else render();
}

export function profileView() {
  const me = S.me!;
  const u = me.user;
  const amb = u.roles.pa || u.roles.sa;
  return `<div class="grid g2" style="align-items:start">
    <div class="stack">${badge(u)}<div class="card">${photoBlock()}</div></div>
    <div class="card stack">
      ${nameFields(u)}
      <div id="emailbox"><label class="fld" for="curEmail">Email (Used to Sign In)</label><div class="row"><input type="email" id="curEmail" value="${esc(u.email)}" disabled style="flex:1"><button class="btn ghost sm" data-act="email-start">Change</button></div></div>
      ${phoneField(u)}
      ${birthdayField(u)}
      ${classOfField(u, false)}
      ${interestsField(u)}
      ${amb ? `<div class="row between wrap"><div><b>Dashboard Calendar Starts In</b><div class="muted small">You can still switch any time.</div></div><div class="seg" role="radiogroup" aria-label="Default calendar view"><button class="${u.defaultView === "month" ? "on" : ""}" data-act="default-view" data-v="month">Calendar</button><button class="${u.defaultView === "list" ? "on" : ""}" data-act="default-view" data-v="list">List</button></div></div>` : ""}
      ${amb ? `<div class="row between"><div><b>Calendar Invites</b><div class="muted small">Off means you'll only get the reconfirmation email.</div></div><label class="toggle"><input type="checkbox" ${u.noInvites ? "" : "checked"} data-change="toggle-invites" aria-label="Calendar invites"><span></span></label></div>` : ""}
      ${profilePushRow()}
      <div class="row between wrap"><div><b>Theme</b></div><div class="seg" role="radiogroup" aria-label="Theme">${(["system", "light", "dark"] as const)
        .map((t) => `<button class="${u.theme === t ? "on" : ""}" data-act="theme" data-v="${t}">${t[0].toUpperCase() + t.slice(1)}</button>`)
        .join("")}</div></div>
      <div class="err" id="err" role="alert"></div>
      <div class="row wrap"><button class="btn" data-act="save-profile">Save</button><button class="btn ghost" data-act="signout">Sign Out</button><button class="btn ghost" data-act="signout-all">Sign Out of All Devices</button></div>
    </div></div>`;
}

async function patch(body: Record<string, unknown>, msg?: string) {
  try {
    S.me = await api<MeResponse>("/api/me", { method: "PATCH", body });
    render();
    if (msg) toast(msg);
  } catch (e) {
    toast((e as Error).message);
  }
}

/* ---- photo: pick, crop to a circle, upload ---- */
let crop: { img: HTMLImageElement; x: number; y: number; z: number } | null = null;

function drawCrop() {
  const cv = $("#cropcv") as HTMLCanvasElement | null;
  if (!cv || !crop) return;
  const ctx = cv.getContext("2d")!;
  const { img, x, y, z } = crop;
  const base = Math.max(cv.width / img.width, cv.height / img.height);
  const w = img.width * base * z, h = img.height * base * z;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, cv.width, cv.height);
  ctx.drawImage(img, (cv.width - w) / 2 + x, (cv.height - h) / 2 + y, w, h);
}

function cropSheet() {
  openSheet(`${edHead("Your Photo")}<div class="edbody">
    <section class="edsec"><h3>Position</h3><p class="muted small" style="margin:0">Drag to move. Use the slider to zoom.</p>
    <div class="cropwrap" id="cropwrap"><canvas id="cropcv" width="600" height="600"></canvas></div>
    <input type="range" min="1" max="4" step="0.01" value="1" data-input="crop-zoom" aria-label="Zoom"></section></div>
    <div class="edfoot"><button class="btn" data-act="upload-photo">Save Photo</button><button class="btn ghost" data-act="close-sheet">Cancel</button>${
      S.me!.user.photo && S.me!.user.profileComplete ? '<button class="btn ghost" style="color:var(--accent);margin-left:auto" data-act="remove-photo">Remove Photo</button>' : ""
    }</div>`);
  drawCrop();
  const wrap = $("#cropwrap")!;
  let drag: { sx: number; sy: number; x: number; y: number } | null = null;
  wrap.addEventListener("pointerdown", (e) => {
    drag = { sx: e.clientX, sy: e.clientY, x: crop!.x, y: crop!.y };
    wrap.setPointerCapture(e.pointerId);
  });
  wrap.addEventListener("pointermove", (e) => {
    if (!drag || !crop) return;
    const scale = 600 / wrap.clientWidth;
    crop.x = drag.x + (e.clientX - drag.sx) * scale;
    crop.y = drag.y + (e.clientY - drag.sy) * scale;
    drawCrop();
  });
  wrap.addEventListener("pointerup", () => (drag = null));
}

on({
  "save-profile": async () => {
    const b = collectProfile();
    if (!b.first || !b.last) return void (($("#err") as HTMLElement).textContent = "Enter your first and last name.");
    if ("interests" in b && !(b.interests as string[]).length) return void (($("#err") as HTMLElement).textContent = "Pick at least one event interest.");
    await patch(b, "Profile saved");
  },
  "default-view": (el) => patch({ defaultView: el.dataset.v }, el.dataset.v === "list" ? "Saved. Your dashboard will open in list view." : "Saved. Your dashboard will open in calendar view."),
  theme: (el) => patch({ theme: el.dataset.v }),
  "toggle-invites": (el) => patch({ noInvites: !(el as HTMLInputElement).checked }, (el as HTMLInputElement).checked ? "Calendar invites are on" : "Calendar invites are off. You'll still get the reconfirmation email."),
  signout: async () => {
    await api("/api/auth/signout", { method: "POST" }).catch(() => {});
    S.me = null;
    location.hash = "";
    render();
    toast("Signed out of this device");
  },
  "signout-all": async () => {
    await api("/api/auth/signout-all", { method: "POST" }).catch(() => {});
    S.me = null;
    location.hash = "";
    render();
    toast("Signed out of all devices");
  },
  "email-start": () => {
    $("#emailbox")!.innerHTML = `<label class="fld" for="newEmail">New Email</label><input type="email" id="newEmail" placeholder="name@example.com" autocomplete="email"><p class="muted small" style="margin:4px 0 8px">We'll send a 6-digit code to the new address. Your email won't change until you enter it.</p><div class="row"><button class="btn sm" data-act="email-send">Send Code</button><button class="btn ghost sm" data-act="email-cancel">Cancel</button></div>`;
    ($("#newEmail") as HTMLInputElement).focus();
  },
  "email-cancel": () => render(),
  "email-send": async () => {
    const email = ($("#newEmail") as HTMLInputElement).value.trim();
    try {
      await api("/api/me/email/code", { body: { email } });
    } catch (e) {
      return toast((e as Error).message);
    }
    $("#emailbox")!.innerHTML = `<label class="fld" for="emCode">Check ${esc(email)}</label><p class="muted small" style="margin:0 0 8px">Enter the 6-digit code we sent. It expires in 15 minutes.</p><input type="text" id="emCode" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="123456" style="max-width:160px;letter-spacing:.2em;font-weight:800" data-email="${esc(email)}"><div class="row" style="margin-top:8px"><button class="btn sm" data-act="email-confirm">Confirm</button><button class="btn ghost sm" data-act="email-cancel">Cancel</button></div>`;
    ($("#emCode") as HTMLInputElement).focus();
  },
  "email-confirm": async () => {
    const inp = $("#emCode") as HTMLInputElement;
    try {
      S.me = await api<MeResponse>("/api/me/email/confirm", { body: { email: inp.dataset.email, code: inp.value } });
      render();
      toast("Email updated. We let your old address know.");
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "pick-photo": (el) => {
    const f = (el as HTMLInputElement).files?.[0];
    (el as HTMLInputElement).value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast("Please pick a photo.");
    const img = new Image();
    img.onload = () => {
      crop = { img, x: 0, y: 0, z: 1 };
      cropSheet();
    };
    img.onerror = () => toast("That photo couldn't be opened. Try a JPEG or PNG.");
    img.src = URL.createObjectURL(f);
  },
  "crop-zoom": (el) => {
    if (crop) crop.z = Number((el as HTMLInputElement).value);
    drawCrop();
  },
  "upload-photo": async () => {
    const src = $("#cropcv") as HTMLCanvasElement;
    const out = document.createElement("canvas");
    out.width = out.height = 512;
    out.getContext("2d")!.drawImage(src, 0, 0, 512, 512);
    const blob: Blob | null = await new Promise((r) => out.toBlob(r, "image/jpeg", 0.88));
    if (!blob) return toast("Couldn't prepare the photo.");
    try {
      await api("/api/me/photo", { method: "PUT", raw: blob, type: "image/jpeg" });
      S.me!.user.photo = true;
      S.photoBust = "?v=" + Date.now();
      closeSheet();
      refreshPhoto();
      toast("Photo saved");
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "remove-photo": async () => {
    try {
      await api("/api/me/photo", { method: "DELETE" });
      closeSheet();
      S.me!.user.photo = false;
      S.photoBust = "?v=" + Date.now();
      refreshPhoto();
      toast("Photo removed");
    } catch (e) {
      toast((e as Error).message);
    }
  },
});
